import { RouteObject } from '@suntex.core/nav'
import { EntityLevel } from '@suntex.fim/enums'
import { flaggedItems, getFlags } from '@suntex.fim/flags'

import VideoPlayer from '../components/VideoPlayer'
import AssessmentReportAlgebraGoalsMetric from '../pages/app/AssessmentReportAlgebraGoalsMetric'
import AssessmentReportBonus from '../pages/app/AssessmentReportBonus'
import AssessmentReportCT from '../pages/app/AssessmentReportCT'
import AssessmentReportEarly from '../pages/app/AssessmentReportEarly'
import AssessmentReportFluencyGames from '../pages/app/AssessmentReportFluencyGames'
import AssessmentReportGoalIndex from '../pages/app/AssessmentReportGoalIndex'
import AssessmentReportGoalIndexByPlayer from '../pages/app/AssessmentReportGoalIndexByPlayer'
import AssessmentReportGoalsAlgebraByPlayer from '../pages/app/AssessmentReportGoalsAlgebraByPlayer'
import AssessmentReportGym from '../pages/app/AssessmentReportGym'
import AssessmentReportJTF from '../pages/app/AssessmentReportJTF'
import AssessmentReportJTFByPlayer from '../pages/app/AssessmentReportJTFByPlayer'
import AssessmentReportKnowAndShow from '../pages/app/AssessmentReportKnowAndShow'
import AssessmentReportMW from '../pages/app/AssessmentReportMW'
import AssessmentReportSkillSetGames from '../pages/app/AssessmentReportSkillSetGames'
import AssessmentReportStagesOfLearning from '../pages/app/AssessmentReportStagesOfLearning'
import AssessmentReportStickerActivity from '../pages/app/AssessmentReportStickerActivity'
import AssessmentReportSummary from '../pages/app/AssessmentReportSummary'
import AssessmentReportVeryImportantFact from '../pages/app/AssessmentReportVeryImportantFact'
import AssessmentReportWDYK2 from '../pages/app/AssessmentReportWDYK2'
import AssessmentReportWDYK2Test from '../pages/app/AssessmentReportWDYK2Test'
import TeacherFAQ from '../pages/app/Help/TeacherFAQ'
import HomeConnection from '../pages/app/LearningCenter/HomeConnection'
import Recognition from '../pages/app/LearningCenter/Recognition'
import ResourcesPage from '../pages/app/LearningCenter/Resources'
import TeacherTutorials from '../pages/app/LearningCenter/TeacherTutorials'
import TeacherMessages from '../pages/app/TeacherMessages'

/**
 * Helper function to create assessment report routes
 */
export const getAssessmentReportRoutes = (
  context: EntityLevel
): RouteObject[] => {
  // Player has limited assessment routes
  if (context === EntityLevel.Player) {
    return [
      {
        path: 'progress',
        Component: AssessmentReportFluencyGames
      },
      {
        path: 'stages-of-learning',
        Component: AssessmentReportStagesOfLearning
      },
      { path: 'just-the-facts', Component: AssessmentReportJTFByPlayer },
      ...flaggedItems(getFlags().player.goals, [
        {
          path: 'algebra-goals-metric',
          Component: AssessmentReportAlgebraGoalsMetric
        }
      ])
    ]
  }

  const baseRoutes: RouteObject[] = [
    { path: 'summary', Component: AssessmentReportSummary },
    { path: 'sticker-count', Component: AssessmentReportStickerActivity },
    { path: 'gym', Component: AssessmentReportGym },
    { path: 'just-the-facts', Component: AssessmentReportJTF },
    {
      path: 'just-the-facts/by-player',
      Component: AssessmentReportJTFByPlayer
    },
    { path: 'know-and-show', Component: AssessmentReportKnowAndShow },
    { path: 'skill-set-games', Component: AssessmentReportSkillSetGames },
    {
      path: 'very-important-facts',
      Component: AssessmentReportVeryImportantFact
    },
    { path: 'early', Component: AssessmentReportEarly },
    { path: 'computational-thinking', Component: AssessmentReportCT },
    { path: 'measurement-world', Component: AssessmentReportMW },
    ...flaggedItems(getFlags().player.goals, [
      { path: 'goals-index', Component: AssessmentReportGoalIndex },
      {
        path: 'algebra-goals-metric',
        Component: AssessmentReportAlgebraGoalsMetric
      }
    ]),
    { path: 'bonuses', Component: AssessmentReportBonus },
    { path: 'fluency-games', Component: AssessmentReportFluencyGames },
    { path: 'what-do-you-know', Component: AssessmentReportWDYK2 }
  ]

  // Context-specific routes
  if (context === EntityLevel.Team) {
    baseRoutes.push(
      ...flaggedItems(getFlags().player.goals, [
        {
          path: 'goals-algebra-by-player',
          Component: AssessmentReportGoalsAlgebraByPlayer
        },
        {
          path: 'goals-by-player',
          Component: AssessmentReportGoalIndexByPlayer
        }
      ]),
      { path: 'what-do-you-know-test', Component: AssessmentReportWDYK2Test }
    )
  } else {
    // school context
    baseRoutes.push(
      ...flaggedItems(getFlags().player.goals, [
        {
          path: 'goals-algebra-by-team',
          Component: AssessmentReportGoalsAlgebraByPlayer
        },
        { path: 'goals-by-team', Component: AssessmentReportGoalIndexByPlayer }
      ])
    )
  }

  return baseRoutes
}

/**
 * Helper function to create common routes for learning center and help
 */
export const getCommonRoutes = (context: EntityLevel): RouteObject[] => {
  const commonRoutes: RouteObject[] = [
    { path: 'messages', Component: TeacherMessages },
    { path: 'help/faq', Component: TeacherFAQ },
    { path: 'video', Component: VideoPlayer }
  ]

  if (context === EntityLevel.Team || context === EntityLevel.School) {
    commonRoutes.push(
      { path: 'learning-center/recognition', Component: Recognition },
      { path: 'learning-center/resources', Component: ResourcesPage },
      { path: 'learning-center/home-connection', Component: HomeConnection },
      { path: 'learning-center/tutorials', Component: TeacherTutorials }
    )
  }

  return commonRoutes
}
